import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide9Practice.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide9Practice({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['senderEmail'],
  })

  const CHOICES = [
    { label: c.choice1Label, safe: false, feedback: c.choice1Feedback },
    { label: c.choice2Label, safe: true, feedback: c.choice2Feedback },
    { label: c.choice3Label, safe: false, feedback: c.choice3Feedback },
    { label: c.choice4Label, safe: false, feedback: c.choice4Feedback },
  ] as const

  const [selected, setSelected] = useState<number | null>(null)

  useEffect(() => {
    if (selected !== null) {
      onComplete()
    }
  }, [selected, onComplete])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:gap-9">
        {/* Email mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-sm"
        >
          <div className="relative">
            <div className="absolute -start-[3px] top-24 h-8 w-[2px] rounded-full bg-slate-600/45" />
            <div className="overflow-hidden rounded-t-[2.5rem] border-[4px] border-b-0 border-slate-900">
              <div className="bg-slate-50">
                <div className="relative flex items-center justify-between px-6 pt-3">
                  <span className="text-[13px] font-semibold text-slate-900">9:41</span>
                  <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 h-[22px] w-[90px] rounded-full bg-slate-800" />
                  <StatusBarIcons />
                </div>

                <div className="px-5 pb-3 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-bold">
                      {c.senderInitial}
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-slate-900">{c.senderName}</p>
                      <p className="text-[12px] text-slate-400">{c.senderEmail}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200/80 bg-white px-5 pb-5 pt-4">
                <p className="text-[15px] font-semibold text-slate-800">
                  {c.emailHeadline}
                </p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.5, ease }}
                >
                  <p className="mt-3 text-[14px] leading-relaxed text-slate-600">
                    {c.emailBody1}
                  </p>
                  <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                    {c.emailBody2}
                  </p>
                  <div className="mt-4 rounded-lg bg-blue-600 py-2.5 text-center">
                    <span className="text-[14px] font-semibold text-white">
                      {c.ctaLabel}
                    </span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Choices */}
        <div className="mx-auto w-full max-w-xl lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.3, ease }}
            className="text-xl sm:text-[1.4rem] font-semibold text-slate-800"
          >
            {c.question}
          </motion.p>
          <div className="mt-5 space-y-3">
            {CHOICES.map((choice, i) => {
              const isSelected = selected === i
              const showResult = selected !== null

              let borderColor = 'border-slate-200 hover:border-slate-300'
              let bg = 'bg-white'
              if (showResult && isSelected) {
                borderColor = choice.safe ? 'border-emerald-400' : 'border-rose-400'
                bg = choice.safe ? 'bg-emerald-50' : 'bg-rose-50'
              } else if (showResult && choice.safe && !isSelected) {
                borderColor = 'border-emerald-200'
                bg = 'bg-emerald-50/50'
              }

              return (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.45 + i * 0.08, ease }}
                  disabled={showResult}
                  onClick={() => setSelected(i)}
                  className={`w-full rounded-xl border-2 ${borderColor} ${bg} px-4 py-3 text-start transition-colors cursor-pointer disabled:cursor-default`}
                >
                  <p className="text-[15px] sm:text-base leading-snug text-slate-700">
                    {choice.label}
                  </p>
                  {showResult && isSelected && (
                    <motion.p
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease }}
                      className={`mt-1.5 text-sm leading-snug ${choice.safe ? 'text-emerald-600' : 'text-rose-500'}`}
                    >
                      {choice.feedback}
                    </motion.p>
                  )}
                  {showResult && !isSelected && choice.safe && (
                    <motion.p
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.15, ease }}
                      className="mt-1.5 text-sm leading-snug text-emerald-600"
                    >
                      {choice.feedback}
                    </motion.p>
                  )}
                </motion.button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function StatusBarIcons() {
  return (
    <div className="flex items-center gap-1">
      <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
        <rect x="1" y="10" width="3" height="5" rx="0.5" />
        <rect x="5" y="7" width="3" height="8" rx="0.5" />
        <rect x="9" y="4" width="3" height="11" rx="0.5" />
        <rect x="13" y="1" width="3" height="14" rx="0.5" opacity="0.3" />
      </svg>
      <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 3C5.5 3 3.3 4 1.8 5.6a.75.75 0 001.1 1C4.2 5.3 6 4.5 8 4.5s3.8.8 5.1 2.1a.75.75 0 001.1-1C12.7 4 10.5 3 8 3z" opacity="0.3" />
        <path d="M8 6.5c-1.7 0-3.2.7-4.3 1.8a.75.75 0 001.1 1C5.7 8.4 6.8 8 8 8s2.3.4 3.2 1.3a.75.75 0 001.1-1C11.2 7.2 9.7 6.5 8 6.5z" opacity="0.6" />
        <path d="M8 10c-.9 0-1.8.4-2.4 1a.75.75 0 001.1 1c.3-.4.8-.5 1.3-.5s1 .2 1.3.5a.75.75 0 001.1-1C9.8 10.4 8.9 10 8 10z" />
      </svg>
      <svg className="h-4.5 w-[22px] text-slate-900" viewBox="0 0 25 12" fill="currentColor">
        <rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
        <rect x="22" y="4" width="2" height="4" rx="0.5" opacity="0.4" />
        <rect x="1.5" y="2.5" width="14" height="7" rx="1" />
      </svg>
    </div>
  )
}
